"use client";

import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import {
  IconArrowRight,
  IconChevronDown,
  IconChevronUp,
} from "@/components/icons";
import { navLabelSx, shadows } from "@/theme/tokens";
import { usePrefersReducedMotion } from "@/components/motion/usePrefersReducedMotion";

export interface MegaMenuItem {
  key: string;
  title: string;
  detail: string;
  cta: string;
  href: string;
  visual: React.ReactNode;
}

/**
 * Header mega menu: a nav trigger that expands a full-width, multi-card panel
 * under the header. Closes on click-away, Escape, or following a link. Each
 * nav item composes this with its own content.
 */
export function MegaMenu({
  label,
  items,
}: {
  label: string;
  items: MegaMenuItem[];
}) {
  const [open, setOpen] = useState(false);
  const reduceMotion = usePrefersReducedMotion();
  const Caret = open ? IconChevronUp : IconChevronDown;

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const close = () => setOpen(false);

  return (
    <ClickAwayListener onClickAway={close}>
      <Box>
        <Link
          component="button"
          type="button"
          underline="none"
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
          aria-haspopup="true"
          sx={{
            ...navLabelSx,
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            whiteSpace: "nowrap",
            color: open ? "primary.main" : "text.secondary",
          }}
        >
          {label}
          <Caret size={16} />
        </Link>

        <AnimatePresence>
          {open && (
            <Box
              component={motion.div}
              initial={reduceMotion ? false : { opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.18, ease: "easeOut" }}
              sx={{
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                backgroundColor: "background.paper",
                borderBottom: "1px solid",
                borderColor: "divider",
                boxShadow: shadows.cardHover,
              }}
            >
              <Container
                maxWidth={items.length < 3 ? "md" : "lg"}
                sx={{
                  py: 4,
                  display: "grid",
                  alignItems: "stretch",
                  gap: 4,
                  gridTemplateColumns: `repeat(${items.length}, 1fr)`,
                }}
              >
                {items.map((item) => (
                  <Box
                    key={item.key}
                    sx={{ display: "flex", flexDirection: "column" }}
                  >
                    {item.visual}
                    <Typography
                      sx={{ fontWeight: 700, fontSize: "1.05rem", mb: 0.75 }}
                    >
                      {item.title}
                    </Typography>
                    <Typography
                      color="text.secondary"
                      sx={{ fontSize: "0.9rem", lineHeight: 1.55, mb: 1.25 }}
                    >
                      {item.detail}
                    </Typography>
                    {/* mt: auto pins every CTA to the same baseline. */}
                    <Link
                      href={item.href}
                      underline="none"
                      onClick={close}
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.9rem",
                        mt: "auto",
                        alignSelf: "flex-start",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      {item.cta}
                      <IconArrowRight size={15} />
                    </Link>
                  </Box>
                ))}
              </Container>
            </Box>
          )}
        </AnimatePresence>
      </Box>
    </ClickAwayListener>
  );
}
